import React, { useState } from 'react';
import { Formiz, FormizStep, useForm } from '@formiz/core';
import { isNumber, isEmail, isPattern } from '@formiz/validations';
import { Field } from './Components/Field';
import { NumericalField } from './Components/NumericalField';
import { FieldSelect } from './Components/FieldSelect';
import { FieldTextArea } from './Components/FieldTextArea';
import { FieldUpload } from './Components/FieldUpload';
import { Checkbox } from './Components/Checkbox';
import { DateField } from './Components/DateField';
import { Radio } from './Components/Radio';
import Modal from './Components/Modal';
import ReactTooltip from 'react-tooltip';
import { ConfigData } from './config';
import CountryDropdownList from './Components/CountryListDropdown';
import CurrencyListDropdown from './Components/CurrencyListDropdown';

const Form = () => {
	const form = useForm({ subscribe: { form: true, fields: true } });
	const [error, setError] = useState(false);
	const [registeredOfficeAddress, setRegisteredOfficeAddress] = useState('');
	const [groupNetworkShow, setGroupNetworkShow] = useState(false);
	const [internationalBrokerShow, setInternationalBrokerShow] = useState(false);
	const [otherLinesShow, setOtherLinesShow] = useState(false);
	const [agencyAgreements, setAgreementAgency] = useState(true);
	const [bankruptcy, setBankruptcy] = useState('');
	const [trustedAccounts, setTrustedAccounts] = useState('');
	const [
		accountsOnCompaniesHouseLabelShow,
		setaccountsOnCompaniesHouseLabelShow,
	] = useState(false);

	const [sOFCount, SetSOFCount] = useState(0);
	const [sOFTouched, SetSOFTouched] = useState(false);
	const [sOFError, SetSOFError] = useState(false);

	const [cOBCount, SetCOBCount] = useState(0);
	const [cOBTouched, SetCOBTouched] = useState(false);
	const [cOBError, SetCOBError] = useState(false);

	const [isSubmitting, SetIsSubmitting] = useState(false);
	const [submitSuccessful, SetSubmitSuccessful] = useState(false);
	const [submitError, SetSubmitError] = useState(false);

	const [declarationConfirmed, setDeclarationConfirmed] = useState(false);
	const [declarationError, setDeclarationError] = useState(false);
	const [declarationTouched, setDeclarationTouched] = useState(false);

	const [showPIIWarning, setShowPIIWarning] = useState(false);
	const [piiConfirmations, setPIIConfirmations] = useState({
		renewalProcess: false,
		sendCopy: false,
	});

	const toBase64 = (file) =>
		new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result);
			reader.onerror = (error) => reject(error);
		});

	const handleRadioChange = (value) => {
		setAgreementAgency(value === 'Yes');
	};

	function handleBrokerType(e) {
		if (e === 'International Broker') {
			setInternationalBrokerShow(true);
			setGroupNetworkShow(false);
		} else if (e === 'Group/Network') {
			setGroupNetworkShow(true);
			setInternationalBrokerShow(false);
		} else {
			setInternationalBrokerShow(false);
			setGroupNetworkShow(false);
		}
	}

	function handleAccountingAddressSameAsRegistered(isChecked) {
		if (isChecked) {
			if (registeredOfficeAddress && registeredOfficeAddress.trim()) {
				form.setFieldsValues({ accountingAddress: registeredOfficeAddress });
			} else {
				form.setFieldsValues({ accountingAddress: '' });
			}
		} else {
			form.setFieldsValues({ accountingAddress: '' });
		}
	}

	function handleTradingAddressSameAsRegistered(isChecked) {
		if (isChecked) {
			if (registeredOfficeAddress && registeredOfficeAddress.trim()) {
				form.setFieldsValues({ tradingAddress: registeredOfficeAddress });
			} else {
				form.setFieldsValues({ tradingAddress: '' });
			}
		} else {
			form.setFieldsValues({ tradingAddress: '' });
		}
	}

	function handleSOFCheckCount(e) {
		SetSOFTouched(true);
		if (e) {
			SetSOFCount(sOFCount + 1);
		} else {
			SetSOFCount(sOFCount - 1);
		}
	}

	function handleCOBCheckCount(e) {
		SetCOBTouched(true);
		if (e === true) {
			SetCOBCount(cOBCount + 1);
		} else {
			SetCOBCount(cOBCount - 1);
		}
	}

	function handleDeclarationConfirmed(e) {
		setDeclarationConfirmed(e);
	}

	function modalClose(e) {
		SetSubmitSuccessful(false);
		SetSubmitError(false);
		if (submitSuccessful) {
			window.location.reload(true);
		}
	}

	const handleSubmitStep = async (event) => {
		event.preventDefault();

		form.submitStep();

		setTimeout(() => {
			if (sOFCount === 0 && !sOFTouched) {
				SetSOFTouched(true);
			}
			if (cOBCount === 0 && !cOBTouched) {
				SetCOBTouched(true);
			}

			if (!declarationConfirmed && form.isLastStep) {
				setDeclarationTouched(true);
				setDeclarationError(true);
				const declarationElement = document.getElementById(
					'declarationConfirmed'
				);
				if (declarationElement) {
					declarationElement.scrollIntoView({
						behavior: 'smooth',
						block: 'center',
					});
				}
				setError('Please confirm the declaration before submitting.');
				return;
			}

			if (
				form.currentStep?.name === '3' &&
				accountsOnCompaniesHouseLabelShow === 'No'
			) {
				const formValues = form.values;
				if (!formValues.firmsFinancialStatements1) {
					const uploadElement = document.querySelector(
						'[name="firmsFinancialStatements1"]'
					);
					if (uploadElement) {
						uploadElement.scrollIntoView({
							behavior: 'smooth',
							block: 'center',
						});
					}
					setError(
						'Please upload the required financial statements for Year 1.'
					);
					return;
				}
			}

			if (!form.currentStep?.isValid) {
				const errorField = document.querySelector(
					'.is-error input, .is-error select, .is-error textarea'
				);
				if (errorField) {
					errorField.scrollIntoView({
						behavior: 'smooth',
						block: 'center',
					});
				}
				setError(
					`Please complete all required fields in ${
						form.currentStep?.name === '1'
							? 'General Information'
							: form.currentStep?.name === '2'
							? 'Regulatory/Licence Status'
							: 'Insurance Monies'
					}.`
				);
				return;
			}

			if (sOFCount === 0) {
				SetSOFError(true);
				if (form.currentStep?.name !== '1') {
					form.goToStep('1');
					setTimeout(() => {
						const sofElement = document.querySelector('[name="sof_Limited"]');
						if (sofElement) {
							sofElement.scrollIntoView({
								behavior: 'smooth',
								block: 'center',
							});
						}
					}, 300);
				} else {
					setTimeout(() => {
						const sofElement = document.querySelector('[name="sof_Limited"]');
						if (sofElement) {
							sofElement.scrollIntoView({
								behavior: 'smooth',
								block: 'center',
							});
						}
					}, 100);
				}
				setError(
					'Please select at least one Status of Firm option in General Information.'
				);
				return;
			}

			if (sOFCount === 0 || sOFCount > 1) {
				SetSOFError(true);
				const sofElement = document.querySelector('[name="sof_Limited"]');
				if (sofElement) {
					sofElement.scrollIntoView({
						behavior: 'smooth',
						block: 'center',
					});
				}
				return;
			}

			if (cOBCount === 0) {
				SetCOBError(true);
				if (form.currentStep?.name !== '1') {
					form.goToStep('1');
					setTimeout(() => {
						const cobElement = document.querySelector(
							'[name="cob_Accident&Health"]'
						);
						if (cobElement) {
							cobElement.scrollIntoView({
								behavior: 'smooth',
								block: 'center',
							});
						}
					}, 300);
				} else {
					setTimeout(() => {
						const cobElement = document.querySelector(
							'[name="cob_Accident&Health"]'
						);
						if (cobElement) {
							cobElement.scrollIntoView({
								behavior: 'smooth',
								block: 'center',
							});
						}
					}, 100);
				}
				setError(
					'Please select at least one Class of Business in General Information.'
				);
				return;
			}

			if (
				showPIIWarning &&
				(!piiConfirmations.renewalProcess || !piiConfirmations.sendCopy)
			) {
				if (form.currentStep?.name !== '3') {
					form.goToStep('3');
					setTimeout(() => {
						const piiElement = document.getElementById(
							'pii_renewal_confirmation'
						);
						if (piiElement) {
							piiElement.scrollIntoView({
								behavior: 'smooth',
								block: 'center',
							});
						}
					}, 300);
				} else {
					setTimeout(() => {
						const piiElement = document.getElementById(
							'pii_renewal_confirmation'
						);
						if (piiElement) {
							piiElement.scrollIntoView({
								behavior: 'smooth',
								block: 'center',
							});
						}
					}, 100);
				}
				setError(
					'Please confirm both PII renewal options in Insurance Monies.'
				);
				return;
			}

			setError(false);

			if (!form.isLastStep) {
				window.scrollTo({ top: 0, behavior: 'smooth' });
			}
		}, 100);
	};
	const submitForm = async (values) => {
		try {
			if (
				accountsOnCompaniesHouseLabelShow === 'No' &&
				!values.firmsFinancialStatements1
			) {
				setError(
					'Please upload the required financial statements for Year 1 before submitting.'
				);
				return;
			}

			SetIsSubmitting(true);
			SetSubmitError(false);
			values.appId = ConfigData.APP_ID;
			values.dateOfSubmission = new Date().toISOString();
			if (values.firmsFinancialStatements1 !== undefined) {
				values.firmsFinancialStatements1 =
					values.firmsFinancialStatements1 !== null
						? await toBase64(values.firmsFinancialStatements1.file)
						: null;
			}
			if (values.firmsFinancialStatements2 !== undefined) {
				values.firmsFinancialStatements2 =
					values.firmsFinancialStatements2 !== null
						? await toBase64(values.firmsFinancialStatements2.file)
						: null;
			}
			if (values.evidenceIndemnityInsurance !== undefined) {
				values.evidenceIndemnityInsurance =
					values.evidenceIndemnityInsurance !== null
						? await toBase64(values.evidenceIndemnityInsurance.file)
						: null;
			}
			if (values.regulatoryLicences !== undefined) {
				values.regulatoryLicences =
					values.regulatoryLicences !== null
						? await toBase64(values.regulatoryLicences.file)
						: null;
			}
			if (values.groupStructureChart !== undefined) {
				values.groupStructureChart =
					values.groupStructureChart !== null
						? await toBase64(values.groupStructureChart.file)
						: null;
			}

			const request = await fetch(ConfigData.API_URL, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': '*',
					'Access-Control-Allow-Methods': 'POST',
					'Ocp-Apim-Subscription-Key': ConfigData.OCP_APIM_SUBSCRIPTION_KEY,
				},
				body: JSON.stringify(values),
			});
			const response = await request.json();
			//console.log('🛰️ payload:', JSON.stringify(values, null, 2));
			SetIsSubmitting(false);
			SetSubmitSuccessful(true);
		} catch (error) {
			SetIsSubmitting(false);
			SetSubmitSuccessful(true);
		}
	};

	const handleDateOfExpiryChange = (value) => {
		if (value) {
			const selectedDate = new Date(value);
			const today = new Date();
			today.setHours(0, 0, 0, 0);

			if (selectedDate < today) {
				setShowPIIWarning(true);
			} else {
				setShowPIIWarning(false);
			}
		} else {
			setShowPIIWarning(false);
		}
	};

	const handleRenewalProcessChange = (value) => {
		setPIIConfirmations({
			renewalProcess: value,
			sendCopy: piiConfirmations.sendCopy,
		});
	};

	const handleSendCopyChange = (value) => {
		setPIIConfirmations({
			renewalProcess: piiConfirmations.renewalProcess,
			sendCopy: value,
		});
	};

	return (
		<div className='Tabs'>
			<Modal
				show={submitSuccessful || submitError}
				handleClose={(e) => modalClose(e)}>
				<h2>
					{submitSuccessful
						? 'Submit Successful'
						: submitError
						? 'Error on Submit'
						: ''}
				</h2>
				<button
					className='button'
					onClick={(e) => modalClose(e)}>
					Close
				</button>
			</Modal>

			<div className='grid-container'>
				<div className='tab-container'>
					{form.steps &&
						form.steps.map((step) => (
							<button
								key={step.name}
								className={`${
									step.name === form.currentStep?.name
										? 'numberCircle active'
										: 'numberCircle'
								}`}
								onClick={() => form.goToStep(step.name)}>
								{step.name}
							</button>
						))}
				</div>
				<div className='tab-container tab-wording'>
					<p>General Information</p>
					<p>Regulatory/Licence Status</p>
					<p>Insurance Monies</p>
				</div>
				{error && (
					<div className='tab-container error'>
						<p>
							<b>
								{typeof error === 'string'
									? error
									: 'Please answer all mandatory fields highlighted'}
							</b>
						</p>
					</div>
				)}
			</div>
			<div className='outlet'>
				<Formiz
					connect={form}
					onValidSubmit={submitForm}>
					<form
						noValidate
						onSubmit={handleSubmitStep}>
						<FormizStep name='1'>
							<div className='row'>
								<div className='column'>
									<FieldSelect
										name='brokerType'
										label='Broker type *'
										required='Broker type is required'
										placeholder='Choose a broker type...'
										onChange={handleBrokerType}
										options={[
											{
												value: 'UK Broker',
												label: 'UK Broker',
											},
											{
												value: 'International Broker',
												label: 'International Broker',
											},
											{
												value: 'Non-Broker Intermediary',
												label: 'Non-Broker Intermediary',
											},
											{
												value: 'Broker Appointed Representative',
												label: 'Broker Appointed Representative',
											},
											{
												value: 'MGA',
												label: 'MGA',
											},
											{
												value: 'Group/Network',
												label: 'Group/Network',
											},
										]}
									/>
								</div>
								<div className='column'></div>
							</div>
							{groupNetworkShow && (
								<div className='row'>
									<div className='column'>
										<FieldSelect
											name='brokerTypeNetworkGroup'
											label='Type of Broker *'
											required='Type of Broker is required'
											placeholder='Choose a broker type...'
											defaultValue=''
											options={[
												{
													value: 'Members',
													label: 'Members',
												},
												{
													value: 'Branch',
													label: 'Branch',
												},
												{
													value: 'Subsidiary',
													label: 'Subsidiary',
												},
												{
													value: 'Franchisee',
													label: 'Franchisee',
												},
											]}
										/>
									</div>
									<div className='column'>
										<Field
											name='brokerGroup'
											label='Name of the Broker Group'
											required='Broker Group Name is required'
										/>
									</div>
								</div>
							)}
							<div className='row'>
								<div className='column'>
									<Field
										name='heardAboutUs'
										label='Where did you hear about us?'
										maxlength='255'
									/>
								</div>
							</div>
							<div className='row'>
								<div className='column'>
									<FieldSelect
										name='sponserType'
										label='Sponsoring DUAL team *'
										required='Sponsoring DUAL team is required'
										placeholder='Choose the underwriting team who directed you to the website....'
										defaultValue=''
										options={[
											{
												value: 'Accident & Health',
												label: 'Accident & Health',
											},
											{
												value: 'Aviation',
												label: 'Aviation',
											},
											{
												value: 'Caribbean Property/Liability',
												label: 'Caribbean Property/Liability',
											},
											{
												value: 'Commercial',
												label: 'Commercial',
											},
											{
												value: "Contractors' Liability",
												label: "Contractors' Liability",
											},
											{
												value: 'Corporate Risks',
												label: 'Corporate Risks',
											},
											{
												value: 'Crisis Management',
												label: 'Crisis Management',
											},
											{
												value: 'DUAL Distribution Team',
												label: 'DUAL Distribution Team',
											},
											{
												value: 'Eridge Underwriting',
												label: 'Eridge Underwriting',
											},
											{
												value: 'Financial Lines',
												label: 'Financial Lines',
											},
											{
												value: 'IPP',
												label: 'IPP',
											},
											{
												value: 'KGM',
												label: 'KGM',
											},
											{
												value: 'Magenta',
												label: 'Magenta',
											},
											{
												value: 'Marine',
												label: 'Marine',
											},
											{
												value: 'Marine Employers Liability',
												label: 'Marine Employers Liability',
											},
											{
												value: 'Political Violence',
												label: 'Political Violence',
											},
											{
												value: 'Private Client',
												label: 'Private Client',
											},
											{
												value: 'Reinsurance',
												label: 'Reinsurance',
											},
											{
												value: 'Title & Legal Indemnity',
												label: 'Title & Legal Indemnity',
											},
											{
												value: 'Travel',
												label: 'Travel',
											},
											{
												value: 'Yacht',
												label: 'Yacht',
											},
											{
												value: 'No specific team',
												label: 'No specific team',
											},
										]}
									/>
								</div>
								<div className='column'></div>
							</div>

							<div className='row'>
								<div className='column'>
									<Field
										name='dualUnderwriter'
										label='DUAL Underwriter/Regional Contact Name'
									/>
								</div>
								<div className='column'></div>
							</div>

							<div className='row'>
								<div className='column'>
									<Field
										name='fullLegalNameOfFirm'
										label='Full legal name of the firm'
										required='Full legal name of the firm is required'
									/>
								</div>
							</div>
							<div className='row'>
								<div className='column'>
									<Field
										name='companyRegistrationNo'
										label='Company registration no. (e.g Companies House)'
										required='Company registration No is required'
										validations={[
											{ rule: isNumber(), message: 'This is not a number' },
										]}
									/>
								</div>
							</div>
							<div className='row'>
								<label className='label-checkbox-main'>
									Status of firm (Please select one)*
								</label>
								<div
									className={`${
										sOFCount === 0 && (sOFTouched || sOFError)
											? 'column is-error round-border'
											: 'column'
									}`}>
									<div className='column'>
										<Checkbox
											id='sof_Limited'
											name='sof_Limited'
											label='Limited'
											required={sOFCount === 0}
											onChange={(e) => handleSOFCheckCount(e)}></Checkbox>
										<Checkbox
											id='sof_SoleTrader'
											name='sof_SoleTrader'
											label='Sole Trader'
											onChange={(e) => handleSOFCheckCount(e)}></Checkbox>
										<Checkbox
											id='sof_Partnership'
											name='sof_Partnership'
											label='Partnership'
											onChange={(e) => handleSOFCheckCount(e)}></Checkbox>
										<Checkbox
											id='sof_Listed'
											name='sof_Listed'
											label='Listed'
											onChange={(e) => handleSOFCheckCount(e)}></Checkbox>
										<Checkbox
											id='sof_Corporation'
											name='sof_Corporation'
											label='Corporation'
											onChange={(e) => handleSOFCheckCount(e)}></Checkbox>
									</div>
								</div>
								{sOFCount === 0 && (sOFTouched || sOFError) && (
									<div className='error-feedback-checkbox'>
										Status of Firm is required
									</div>
								)}
								{sOFCount > 1 && (sOFTouched || sOFError) && (
									<div className='error-feedback-checkbox'>
										Please select only one Status of Firm
									</div>
								)}
								<div className='column'></div>
							</div>
							<div className='row'>
								<div className='column'>
									<FieldTextArea
										name='officeAddress'
										label='Registered office address'
										required='Office address is required'
										onChange={(value) => {
											setRegisteredOfficeAddress(value || '');
										}}
									/>
								</div>
								<div className='column'>
									<FieldSelect
										name='registeredOffice'
										label='Registered office country *'
										required='Registered office country is required'
										placeholder='Search for a registered office country...'
										options={CountryDropdownList}
									/>
								</div>
							</div>
							<div className='row'>
								<div className='column'>
									<Field
										name='tradingNames'
										label='Trading names, assumed names and/or other business names'
										placeholder='If different from legal name of the firm'
									/>
								</div>
								<div className='column'>
									<FieldTextArea
										name='tradingAddress'
										label='Trading address (All trading addresses)'
										required='Trading address is required'
									/>
									<div className='column'>
										<Checkbox
											id='sameAsRegisteredAddress'
											name='tradingAddress'
											label='Same as registered address'
											onChange={(e) =>
												handleTradingAddressSameAsRegistered(e)
											}></Checkbox>
									</div>
								</div>
							</div>
							<div className='row'>
								<div className='column'>
									<FieldTextArea
										name='tradingBranchAddress'
										label='Trading branch addresses (optional)'
									/>
								</div>
							</div>
							<div className='row'>
								<div className='column'>
									<FieldTextArea
										name='accountingAddress'
										label='Accounting address'
										placeholder='Including branches and accounting offices'
										required='Accounting address is required'
									/>
									<div className='column'>
										<Checkbox
											id='sameAsRegisteredAddress'
											name='accountingAddress'
											label='Same as registered address'
											onChange={(e) =>
												handleAccountingAddressSameAsRegistered(e)
											}></Checkbox>
									</div>
								</div>
								<div className='row'></div>
								<div className='column'>
									<Field
										name='accountingEmail'
										label='Accounting contact email'
										required='Accounting contact email is required'
										validations={[
											{ rule: isEmail(), message: 'This is not a valid email' },
										]}
									/>
								</div>
							</div>
							<div className='row'>
								<div className='column'>
									<Field
										name='contactName'
										label='Contact name'
										required='Contact name is required'
									/>
								</div>
								<div className='column'>
									<Field
										name='contactPhoneNumber'
										label='Contact phone number'
										required='Contact phone number is required'
										validations={[
											{ rule: isNumber(), message: 'This is not a number' },
										]}
									/>
								</div>
							</div>
							<div className='row'>
								<div className='column'>
									<Field
										name='contactEmail'
										label='Contact email'
										required='Contact email is required'
										validations={[
											{ rule: isEmail(), message: 'This is not a valid email' },
										]}
									/>
								</div>
								<div className='column'>
									<Field
										name='website'
										label='Website'
										required='Website is required'
									/>
								</div>
							</div>
							{internationalBrokerShow && (
								<div className='row'>
									<div className='column'>
										<FieldTextArea
											name='directors'
											label='Names of directors/partners/principals'
											required='Names of directors/partners/principals is required'
										/>
									</div>
									<div className='column'></div>
								</div>
							)}
							<div className='row'>
								<label className='label-checkbox-main'>
									Classes of business to be shown *
								</label>
								<div
									className={`${
										cOBCount === 0 && (cOBTouched || cOBError)
											? 'column is-error round-border'
											: 'column'
									}`}>
									<div className='column'>
										<Checkbox
											id='cob_BloodstockEquine'
											name='cob_BloodstockEquine'
											label='Bloodstock & Equine'
											onChange={(e) => handleCOBCheckCount(e)}
										/>
										<Checkbox
											id='cob_ClimateRisk'
											name='cob_ClimateRisk'
											label='Climate Risk & Resilience'
											onChange={(e) => handleCOBCheckCount(e)}
										/>
										<Checkbox
											id='cob_CommercialCombined'
											name='cob_CommercialCombined'
											label='Commercial Combined'
											onChange={(e) => handleCOBCheckCount(e)}
										/>
										<Checkbox
											id='cob_Construction'
											name='cob_Construction'
											label='Construction'
											onChange={(e) => handleCOBCheckCount(e)}
										/>
										<Checkbox
											id='cob_CorporateProperty'
											name='cob_CorporateProperty'
											label='Corporate Property'
											onChange={(e) => handleCOBCheckCount(e)}
										/>
										<Checkbox
											id='cob_CrisisManagement'
											name='cob_CrisisManagement'
											label='Crisis Management'
											onChange={(e) => handleCOBCheckCount(e)}
										/>
										<Checkbox
											id='cob_Cyber'
											name='cob_Cyber'
											label='Cyber'
											onChange={(e) => handleCOBCheckCount(e)}
										/>
										<Checkbox
											id='cob_DirectorsOfficers'
											name='cob_DirectorsOfficers'
											label='Directors & Officers'
											onChange={(e) => handleCOBCheckCount(e)}
										/>
										<Checkbox
											id='cob_EndSupplierFailure'
											name='cob_EndSupplierFailure'
											label='End Supplier Failure'
											onChange={(e) => handleCOBCheckCount(e)}
										/>
										<Checkbox
											id='cob_FinancialInstitutions'
											name='cob_FinancialInstitutions'
											label='Financial Institutions'
											onChange={(e) => handleCOBCheckCount(e)}
										/>
										<Checkbox
											id='cob_MarineLiability'
											name='cob_MarineLiability'
											label='Marine Liability'
											onChange={(e) => handleCOBCheckCount(e)}
										/>
									</div>

									<div className='column'>
										<Checkbox
											id='cob_MergersAcquisitions'
											name='cob_MergersAcquisitions'
											label='Mergers & Acquisitions'
											onChange={(e) => handleCOBCheckCount(e)}
										/>
										<Checkbox
											id='cob_ProfessionalIndemnity'
											name='cob_ProfessionalIndemnity'
											label='Professional Indemnity'
											onChange={(e) => handleCOBCheckCount(e)}
										/>
										<Checkbox
											id='cob_Reinsurance'
											name='cob_Reinsurance'
											label='Reinsurance'
											onChange={(e) => handleCOBCheckCount(e)}
										/>
										<Checkbox
											id='cob_ScheduledAirlineFailure'
											name='cob_ScheduledAirlineFailure'
											label='Scheduled Airline Failure'
											onChange={(e) => handleCOBCheckCount(e)}
										/>
										<Checkbox
											id='cob_SocialCare'
											name='cob_SocialCare'
											label='Social Care'
											onChange={(e) => handleCOBCheckCount(e)}
										/>
										<Checkbox
											id='cob_SpecialistHome'
											name='cob_SpecialistHome'
											label='Specialist Home'
											onChange={(e) => handleCOBCheckCount(e)}
										/>
										<Checkbox
											id='cob_SpecialistMotor'
											name='cob_SpecialistMotor'
											label='Specialist Motor'
											onChange={(e) => handleCOBCheckCount(e)}
										/>
										<Checkbox
											id='cob_TitleLegalIndemnity'
											name='cob_TitleLegalIndemnity'
											label='Title & Legal Indemnity'
											onChange={(e) => handleCOBCheckCount(e)}
										/>
										<Checkbox
											id='cob_TourOperatorsFailure'
											name='cob_TourOperatorsFailure'
											label='Tour Operators Financial Failure'
											onChange={(e) => handleCOBCheckCount(e)}
										/>
										<Checkbox
											id='cob_UnoccupiedProperty'
											name='cob_UnoccupiedProperty'
											label='Unoccupied Property'
											onChange={(e) => handleCOBCheckCount(e)}
										/>
										<Checkbox
											id='cob_OtherLines'
											name='cob_OtherLines'
											label='Other Lines'
											onChange={(e) => {
												handleCOBCheckCount(e);
												setOtherLinesShow(e);
											}}
										/>
									</div>
								</div>
								{cOBCount === 0 && (cOBTouched || cOBError) && (
									<label className='error-feedback-checkbox'>
										Classes of business is required
									</label>
								)}
							</div>
							{otherLinesShow && (
								<div className='row'>
									<div className='column'>
										<Field
											name='otherLines_COB_Detail'
											label='Please provide class of business details'
											required='Other lines class of business required'
										/>
									</div>
									<div className='column'></div>
								</div>
							)}
							<div className='row'>
								<div className='column'>
									<Field
										name='estimatedGWP'
										label='Estimated GWP (Gross Written Premium)'
										required='GWP is required'
										placeholder='e.g., 1,000,000'
										validations={[
											{
												rule: isPattern(/^[\d,]+$/, { strict: false }),
												message: 'Please enter a valid number (commas allowed)',
											},
										]}
									/>
								</div>
								<div className='column'>
									<FieldSelect
										name='gwp_currency'
										label='Currency *'
										required='Currency is required'
										placeholder='Search for currency...'
										options={CurrencyListDropdown}
									/>
								</div>
							</div>
							<div className='row'></div>
							<div className='row'>
								<div className='column'>
									<FieldTextArea
										name='softwareSystems'
										label='Which software systems do you use for your admin, quotations and/or training?'
										placeholder='Please include your SCID number(s) and Mailbox number(s) where applicable'
										required='This field is required.'
									/>
								</div>
							</div>
							<div classname='row'></div>
							<div className='row'>
								<div className='column'>
									<Radio
										name='agencyAgreements'
										label='Is the firm party to any agency agreements or binding authorities?'
										required='Yes or no is required'
										onChange={(value) => setAgreementAgency(value)}
										showPopup={false}
									/>
								</div>
								{agencyAgreements === 'Yes' && (
									<div className='column'>
										<FieldTextArea
											name='agencyAgreementsMore'
											label='Please provide details'
										/>
									</div>
								)}
							</div>
							<div className='row'>
								<div className='column'>
									<Radio
										name='lloydsApprovedBroker'
										label="Is the firm a Lloyds's approved broker?"
										required='Yes or no is required'
										showPopup={false}
									/>
								</div>
								<div className='column'></div>
							</div>
							<div className='row'>
								<div className='column'></div>
								<div className='column'></div>
							</div>
						</FormizStep>

						<FormizStep name='2'>
							<div className='row'>
								<div className='column'>
									<Field
										name='nameOfAgency'
										label='Name of the agency or governing authority by whom the Firm is registered or regulated'
										required='Name of the agency is required'
									/>
								</div>
								<div className='column'>
									<Field
										name='websiteOfRegulatingBody'
										label='Website address of the regulating body'
										required='Website address of the regulating body is required'
									/>
								</div>
							</div>
							<div className='row'>
								<div className='column'>
									<FieldSelect
										name='regulatoryCountry'
										label='Country'
										required='Country is required'
										placeholder='Search for a country...'
										options={CountryDropdownList}
									/>
								</div>
								<div className='column'>
									<Field
										name='registrationNumber'
										label='FCA Number OR Local Regulator Reference Number'
										required='Registration number is required'
									/>
								</div>
							</div>
						</FormizStep>

						<FormizStep name='3'>
							<div className='row'>
								<div className='column'>
									<Radio
										name='premiumsInSeperateAccounts'
										label='Are premium or other funds owned by (re)insureds and/or (re)insurers (fiduciary funds) kept in separate accounts to other funds i.e. not mingled with other funds?'
										required='Yes or No is required'
										showPopup={false}
									/>
								</div>
								<div className='column'></div>
							</div>
							<div className='row'>
								<div className='column'>
									<Radio
										name='trustedAccounts'
										label="Are these accounts trust accounts i.e. would the beneficial owners be protected in the event of the firm's insolvency?"
										required='Yes or no is required'
										onChange={(value) => setTrustedAccounts(value)}
										showPopup={false}
									/>
								</div>
								{trustedAccounts === 'No' && (
									<div className='column'>
										<FieldTextArea
											name='trustedNo'
											label='If no, please describe how beneficial owners are protected'
										/>
									</div>
								)}
								<div className='column'></div>
							</div>
							<div className='row'>
								<div className='column'>
									<Radio
										name='bankruptcy'
										label='Has the firm (or its principal shareholders, directors, officers, employees or persons representing the Firm) or affiliated organisation or any predecessor business filed for bankruptcy in the past five years?'
										required='Yes or no is required'
										onChange={(value) => setBankruptcy(value)}
										showPopup={false}
									/>
								</div>
								{bankruptcy === 'Yes' && (
									<div className='column'>
										<FieldTextArea
											name='bankruptcyYes'
											label='If yes, please describe the circumstances'
										/>
									</div>
								)}
							</div>
							<div className='row'>
								<div className='column'>
									<Radio
										name='accountsOnCompaniesHouse'
										label="If you are in the UK, are your latest financial accounts available on Companies House website? If yes, we can obtain your financial accounts from UK Companies House website - select yes below. If they are not available or you are not in the UK, please  select no and attach two years' audited company financial accounts. If you are a new company and these accounts are not available yet, please provide one year of projected financial accounts."
										required='Yes or no is required'
										onChange={(value) =>
											setaccountsOnCompaniesHouseLabelShow(value)
										}
										showPopup={false}
									/>
								</div>
							</div>
							{accountsOnCompaniesHouseLabelShow === 'No' && (
								<div className='row'>
									<label>
										&nbsp;&nbsp;&nbsp;Please attach last 2 years of your
										company's financial accounts or if you are a new company
										please provide 1 year projected Financial accounts.
									</label>
									<div className='row'>
										<div className='column'>
											<label>
												Please accompany all original supporting documents with
												translations in English.
											</label>
										</div>
									</div>
									<div className='row'>
										<div className='column2'>
											<label>
												The firm's latest financial statements - Year 1 *
											</label>
											<FieldUpload
												name='firmsFinancialStatements1'
												required='Financial statements for most recent year are required'
												label='If you are a new firm with less than one year of trading accounts, please provide one year of projected financials'
												data-tip
												data-for='financialTip1'
											/>
											<ReactTooltip
												id='financialTip'
												place='top'
												effect='solid'
												className='tooltip'
												uuid='uuid-for-tooltip-arrow'>
												Only valid file formats are .DOC, .DOCX and .PDF
											</ReactTooltip>
										</div>
									</div>
									<div className='row'>
										<div className='column2'>
											<label>
												The firm's latest financial statements - Year 2
											</label>
											<FieldUpload
												name='firmsFinancialStatements2'
												label='If your latest financial accounts are not available, or you are not in the UK, please attach another year of audited company financial accounts'
												data-tip
												data-for='financialTip2'
											/>
											<ReactTooltip
												id='financialTip'
												place='top'
												effect='solid'
												className='tooltip'
												uuid='uuid-for-tooltip-arrow'>
												Only valid file formats are .DOC, .DOCX and .PDF
											</ReactTooltip>
										</div>
									</div>
								</div>
							)}
							<div className='row'>
								<div className='column'>
									<Radio
										name='policiesUpToDate'
										label='Can you confirm that your company policies, including the business continuity plan, vulnerable customer policy, and consumer duty policy, are up to date?.'
										required='Yes or no is required'
										showPopup={false}
									/>
								</div>
							</div>
							<div className='row'>
								<div className='column'>
									<DateField
										name='dateOfExpiry'
										label='Date of expiry of professional indemnity insurance'
										required='Date of expiry of professional indemnity insurance is required'
										onChange={handleDateOfExpiryChange}
									/>
									{showPIIWarning && (
										<div className='row'>
											<div className='column'>
												<div className='warning-message'>
													<h4>Professional Indemnity Insurance Expired</h4>
													<p>
														You have selected a date in the past. Please confirm
														the following:
													</p>

													<div>
														<Checkbox
															id='pii_renewal_confirmation'
															name='pii_renewal_confirmation'
															label='I confirm that we are in the process of renewing the PII/obtaining new PII'
															onChange={(value) =>
																handleRenewalProcessChange(value)
															}
														/>
													</div>

													<div>
														<Checkbox
															id='pii_copy_confirmation'
															name='pii_copy_confirmation'
															label='I confirm that we will send a copy of this once arranged'
															onChange={(value) => handleSendCopyChange(value)}
														/>
													</div>
													{error &&
														showPIIWarning &&
														(!piiConfirmations.renewalProcess ||
															!piiConfirmations.sendCopy) && (
															<div className='pii-error-feedback'>
																Both confirmations are required when PII has
																expired
															</div>
														)}
													<div>
														<strong>Note:</strong> Your application may not be
														processed until the new PII documentation has been
														received.
													</div>
												</div>
											</div>
										</div>
									)}
								</div>
								<div className='row'>
									<div className='column2'>
										<FieldUpload
											name='evidenceIndemnityInsurance'
											label="Evidence of the firm's professional indemnity insurance *"
											required="Evidence of the firm's professional indemnity insurance is required"
											data-tip
											data-for='evidenceTip'
										/>
									</div>
									<ReactTooltip
										id='evidenceTip'
										place='top'
										effect='solid'
										className='tooltip'
										uuid='uuid-for-tooltip-arrow'>
										Only valid file formats are .DOC, .DOCX and .PDF
									</ReactTooltip>
									<div className='column2'>
										<FieldUpload
											name='regulatoryLicences'
											label='Copies of any current regulatory/trading licences *'
											required='Copies of any current regulatory/trading licences is required'
											data-tip
											data-for='regulatoryTip'
										/>
									</div>
									<ReactTooltip
										id='regulatoryTip'
										place='top'
										effect='solid'
										className='tooltip'
										uuid='uuid-for-tooltip-arrow'>
										Only valid file formats are .DOC, .DOCX and .PDF
									</ReactTooltip>
								</div>
							</div>
							<div className='row'>
								<div className='column'>
									<FieldUpload
										name='groupStructureChart'
										label='Upload Group Structure Chart *'
										required='Group structure chart is required'
										data-tip
										data-for='groupStructureTip'
									/>
								</div>
								<ReactTooltip
									id='groupStructureTip'
									place='top'
									effect='solid'
									className='tooltip'
									uuid='uuid-for-tooltip-arrow'>
									Only valid file formats are .DOC, .DOCX and .PDF
								</ReactTooltip>
							</div>
							<div className='row'>
								<div className='column'>
									<Field
										name='submitterName'
										label='Submitter name'
										required='Name of submitter is required'
									/>
								</div>
							</div>
							<div className='row'></div>
							<div className='row'>
								<div className='column'>
									<label>Declarations *</label>
									<div className='declaration-bullets'>
										<label>
											This declaration must be completed by a person suitably
											authorized to do so by the applicant firm
										</label>
										<div className='row'></div>
										<label>
											I confirm that the information in this application is
											accurate and complete to the best of my knowledge and
											belief and that I have taken all reasonable steps to
											ensure that this is the case.
										</label>
										<div className='row'></div>
										<label>
											I give DUAL Corporate Risks Limited permission to contact
											the other Principals where necessary for whom I am an
											Appointed Representative (if applicable)
										</label>
										<div className='row'></div>
										<label>
											I confirm that DUAL Corporate Risks Limited have
											permission to carry out required checks such as sanction
											screening and credit checks at their required frequency
										</label>
										<div className='row'></div>
										<label>
											I acknowledge and agree that any information provided
											pursuant to this application will be processed as set out
											in DUAL’s privacy policy:{' '}
											<a
												className='policyLink'
												target='_blank'
												href='https://www.dualgroup.com/privacy-and-data-protection-policy'>
												https://www.dualgroup.com/privacy-and-data-protection-policy
											</a>
											.
										</label>
									</div>
								</div>
							</div>
							<div className='row'>
								<label className='label-checkbox-main'>
									Declaration Confirmation *
								</label>
								<div
									className={`${
										!declarationConfirmed &&
										(declarationTouched || declarationError)
											? 'column is-error round-border'
											: 'column'
									}`}>
									<Checkbox
										id='declarationConfirmed'
										name='declarationConfirmed'
										label='I confirm all of the above'
										required={declarationConfirmed === 0}
										onChange={(e) => handleDeclarationConfirmed(e)}></Checkbox>
								</div>
								{!declarationConfirmed &&
									(declarationTouched || declarationError) && (
										<label className='error-feedback-checkbox'>
											You must confirm you have read all declarations before
											continuing.
										</label>
									)}
							</div>
							<div className='row'>
								<div className='column'>
									<DateField
										name='dateOfDeclaration'
										label='Please submit the date you are declaring the above to be true.'
										required='Date of decleration is required'
									/>
								</div>
							</div>
							<div className='row'>
								<div className='column'>
									<Field
										name='positionHeld'
										label='Position held'
										required='Position held is required'
									/>
								</div>
								<div className='column'></div>
							</div>
						</FormizStep>

						{!form.isFirstStep && (
							<button
								type='button'
								className='button'
								onClick={form.prevStep}>
								Back
							</button>
						)}
						{form.isLastStep ? (
							<button
								type='submit'
								className='button'
								onClick={submitForm}>
								{isSubmitting ? 'Submitting...' : 'Submit'}
							</button>
						) : (
							<button
								type='submit'
								className='button'>
								Continue
							</button>
						)}
					</form>
				</Formiz>
			</div>
		</div>
	);
};

export default Form;
